import React, { useEffect, useState } from 'react';
import { Mail, Plus, Edit, Trash2, Eye, Clock, CheckCircle, XCircle, Send, X, Save } from 'lucide-react';
import { supabaseService } from '@/shared/services/supabaseService';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { userProfileSyncService } from '@/shared/services/userProfileSyncService';

interface EmailTemplate {
  id: string;
  user_id: string;
  name: string;
  subject: string;
  body: string;
  type: 'invoice' | 'quote' | 'receipt' | 'welcome' | 'custom';
  is_default: boolean;
  is_active: boolean;
  created_at: string;
}

interface EmailConfig {
  provider: string;
  from_email: string;
  from_name: string;
  reply_to_email: string;
  notifications_enabled: boolean;
}

const AdminEmailView: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    type: 'custom' as EmailTemplate['type'],
    is_active: true,
    is_default: false
  });

  const [config, setConfig] = useState<EmailConfig>({
    provider: 'Resend',
    from_email: 'noreply@swaysuite.com',
    from_name: 'SwaySuite',
    reply_to_email: 'support@swaysuite.com',
    notifications_enabled: true
  });

  useEffect(() => {
    loadEmailTemplates();
    loadEmailConfig();
  }, []);

  const loadEmailTemplates = async () => {
    try {
      const { data, error } = await supabaseService.client
        .from('email_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading email templates:', error);
      } else {
        setTemplates(data || []);
      }
    } catch (error) {
      console.error('Failed to load email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailConfig = () => {
    // Load config from localStorage or use defaults
    const savedConfig = localStorage.getItem('email_config');
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig));
    }
  };

  const handleViewTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      type: template.type,
      is_active: template.is_active,
      is_default: template.is_default
    });
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: `Hello {{client_name}},

Thank you for your business.

Best regards`,
      type: 'custom',
      is_active: true,
      is_default: false
    });
    setModalMode('create');
    setModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      // Get Firebase auth user
      const firebaseUser = await firebaseAuthService.getCurrentUser();

      if (!firebaseUser) {
        alert('You must be logged in to create templates');
        return;
      }

      // Get user profile from Supabase using Firebase user
      const profile = await userProfileSyncService.getOrCreateSupabaseProfile(firebaseUser);

      if (!profile) {
        alert('Failed to get user profile');
        return;
      }

      const templateData = {
        user_id: profile.id,
        name: formData.name,
        subject: formData.subject,
        body: formData.body,
        type: formData.type,
        is_active: formData.is_active,
        is_default: formData.is_default
      };

      if (modalMode === 'create') {
        const { error } = await supabaseService.client
          .from('email_templates')
          .insert(templateData);

        if (error) throw error;
      } else if (modalMode === 'edit' && selectedTemplate) {
        const { error } = await supabaseService.client
          .from('email_templates')
          .update(templateData)
          .eq('id', selectedTemplate.id);

        if (error) throw error;
      }

      await loadEmailTemplates();
      setModalOpen(false);
      alert('Template saved successfully!');
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(`Failed to save template: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      const { error } = await supabaseService.client
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadEmailTemplates();
      setDeleteConfirm(null);
      alert('Template deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting template:', error);
      alert(`Failed to delete template: ${error.message}`);
    }
  };

  const handleSaveConfig = () => {
    localStorage.setItem('email_config', JSON.stringify(config));
    alert('Email configuration saved successfully!');
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      invoice: 'bg-blue-100 text-blue-700',
      quote: 'bg-emerald-100 text-emerald-700',
      receipt: 'bg-purple-100 text-purple-700',
      welcome: 'bg-amber-100 text-amber-700',
      custom: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || colors.custom;
  };

  const previewVariables = {
    client_name: 'John Doe',
    company_name: 'Acme Inc',
    invoice_number: 'INV-001',
    quote_number: 'QUOTE-001',
    amount: '$1,000.00',
    due_date: 'December 31, 2025'
  };

  const renderPreview = (body: string, subject: string) => {
    let previewBody = body;
    let previewSubject = subject;

    // Replace variables with preview values
    Object.entries(previewVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'gi');
      previewBody = previewBody.replace(regex, value);
      previewSubject = previewSubject.replace(regex, value);
    });

    return { previewBody, previewSubject };
  };

  const stats = {
    total: templates.length,
    active: templates.filter(t => t.is_active).length,
    default: templates.filter(t => t.is_default).length
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Email Management</h2>
          <p className="text-gray-500 text-sm">Manage email templates and configuration</p>
        </div>
        <button
          onClick={handleCreateTemplate}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded font-medium hover:bg-gray-800 transition-colors text-sm"
        >
          <Plus size={14} />
          New Template
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Mail size={14} />
            <span className="font-medium">Total Templates</span>
          </div>
          <p className="text-xl font-semibold text-gray-900">{stats.total}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <CheckCircle size={14} />
            <span className="font-medium">Active</span>
          </div>
          <p className="text-xl font-semibold text-gray-900">{stats.active}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Send size={14} />
            <span className="font-medium">Default</span>
          </div>
          <p className="text-xl font-semibold text-gray-900">{stats.default}</p>
        </div>
      </div>

      {/* Email Templates Table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 text-sm">Email Templates</h3>
          <p className="text-xs text-gray-500">Manage email templates for different purposes</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center mb-3">
              <Mail size={20} className="text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm mb-2">No Email Templates</h3>
            <p className="text-gray-500 max-w-md mb-4 text-xs">
              Create email templates to customize communications sent to your clients.
            </p>
            <button
              onClick={handleCreateTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded font-medium hover:bg-gray-800 transition-colors text-sm"
            >
              <Plus size={14} />
              Create First Template
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{template.name}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">{template.subject}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(template.type)}`}>
                        {template.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {template.is_default && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                            Default
                          </span>
                        )}
                        {template.is_active ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                            <CheckCircle size={12} />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400 text-xs font-medium">
                            <XCircle size={12} />
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Clock size={12} />
                        {new Date(template.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Preview"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(template.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Configuration */}
      <div className="bg-white border border-gray-200 rounded p-5">
        <div className="mb-5">
          <h3 className="font-semibold text-gray-900 text-sm">Email Configuration</h3>
          <p className="text-xs text-gray-500">Manage email service provider settings</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Email Provider</label>
              <select
                value={config.provider}
                onChange={(e) => setConfig({ ...config, provider: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
              >
                <option>Resend</option>
                <option>SendGrid</option>
                <option>Mailgun</option>
                <option>SMTP</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">From Email</label>
              <input
                type="email"
                value={config.from_email}
                onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                placeholder="noreply@swaysuite.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">From Name</label>
              <input
                type="text"
                value={config.from_name}
                onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                placeholder="SwaySuite"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Reply-To Email</label>
              <input
                type="email"
                value={config.reply_to_email}
                onChange={(e) => setConfig({ ...config, reply_to_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                placeholder="support@swaysuite.com"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                <p className="text-xs text-gray-500">Receive notifications for email events</p>
              </div>
              <button
                onClick={() => setConfig({ ...config, notifications_enabled: !config.notifications_enabled })}
                className={`relative inline-flex h-5 w-9 items-center rounded transition-colors ${config.notifications_enabled ? 'bg-gray-900' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded bg-white transition-transform ${config.notifications_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={handleSaveConfig}
            className="px-4 py-2 bg-gray-900 text-white rounded font-medium hover:bg-gray-800 transition-colors text-sm"
          >
            Save Configuration
          </button>
        </div>
      </div>

      {/* Template Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">
                {modalMode === 'view' ? 'Preview Template' : modalMode === 'edit' ? 'Edit Template' : 'New Template'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-5">
              {modalMode === 'view' && selectedTemplate ? (
                <div className="space-y-5">
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{renderPreview(selectedTemplate.body, selectedTemplate.subject).previewSubject}</h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(selectedTemplate.type)}`}>
                        {selectedTemplate.type}
                      </span>
                      {selectedTemplate.is_default && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-4">
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">
                        {renderPreview(selectedTemplate.body, selectedTemplate.subject).previewBody}
                      </pre>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-xs font-medium text-blue-900 mb-2">Available Variables:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                      {Object.keys(previewVariables).map((key) => (
                        <code key={key} className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {'{{' + key + '}}'}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Template Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                        placeholder="Invoice Reminder"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as EmailTemplate['type'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                      >
                        <option value="custom">Custom</option>
                        <option value="invoice">Invoice</option>
                        <option value="quote">Quote</option>
                        <option value="receipt">Receipt</option>
                        <option value="welcome">Welcome</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Subject</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                      placeholder="Your invoice from {{company_name}}"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Body</label>
                    <textarea
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors font-mono"
                      placeholder="Hello {{client_name}},"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-xs font-medium text-blue-900 mb-2">Available Variables:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                      {Object.keys(previewVariables).map((key) => (
                        <code key={key} className="bg-blue-100 text-blue-800 px-2 py-1 rounded cursor-pointer hover:bg-blue-200 transition-colors">
                          {'{{' + key + '}}'}
                        </code>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 accent-gray-900 bg-white border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-gray-500/50"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_default}
                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                        className="w-4 h-4 accent-gray-900 bg-white border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-gray-500/50"
                      />
                      <span className="text-sm font-medium text-gray-700">Default</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              {modalMode !== 'view' && (
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving || !formData.name || !formData.subject}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded shadow-lg max-w-md w-full p-5 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                <Trash2 size={16} className="text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">Delete Template</h3>
            </div>
            <p className="text-gray-600 mb-5 text-sm">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteTemplate(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailView;
